import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { SERVER_URL } from "../../../../utils/serverConfig";
import { fmtDur, fmtClock } from "./bits";

/* Where the time went — made answerable.

   "Chrome · 3h 12m" is an accounting fact and a dead end. Opening a row asks
   the obvious follow-up — what was being DONE in those three hours — and the
   answer arrives in two layers: the measured record instantly, then the
   written account of the distinct purposes the application was put to.

   Everything here is scoped to that one application. */

/**
 * Split written prose into sentences for display.
 *
 * Same boundary rule the drill views use (StoryChrome.splitBeats): break after
 * .!? only when the next sentence actually starts. Kept local rather than
 * imported so this tree carries no dependency on the graphical drill.
 *
 * Abbreviations are protected first, otherwise "teamgrid.ai/dashboard" and
 * "e.g." split mid-sentence and the paragraphs come out shredded.
 */
function splitSentences(text) {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return [];
  const DOT = "\u0000"; // sentinel: cannot occur in written prose
  const parts = raw
    .replace(/(\w)\.(\w)/g, `$1${DOT}$2`)   // teamgrid.ai, index.js, e.g.
    .split(/(?<=[.!?])\s+(?=["\u201C\u2018']?[A-Z0-9])/)
    .map((s) => s.split(DOT).join(".").trim())
    .filter(Boolean);
  return parts.length ? parts : [raw];
}

/** Shimmering placeholder, shaped like the content it stands in for. */
function Sk({ w = "100%", h = 12, r = 6, style }) {
  return <span className="wsn-shimmer" style={{ display: "block", width: w, height: h, borderRadius: r, ...style }} />;
}

function AppSkeleton({ tone, t, sf }) {
  return (
    <div style={{
      "--wsn-sk": sf.inset,
      "--wsn-sk-hi": t.isDark ? "rgba(255,255,255,.075)" : "rgba(24,32,46,.055)",
    }}>
      <div style={{ display: "flex", gap: 22, marginBottom: 18 }}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} style={{ flex: 1 }}>
            <Sk w="60%" h={9} r={4} />
            <Sk w="80%" h={17} r={6} style={{ marginTop: 7 }} />
          </span>
        ))}
      </div>
      <Sk w="88%" h={14} r={6} style={{ marginBottom: 18 }} />
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: `${tone}44`, flex: "none", marginTop: 5 }} />
          <span style={{ flex: 1 }}>
            <Sk w={i % 2 ? "52%" : "64%"} h={13} r={5} />
            <Sk w="78%" h={11} r={5} style={{ marginTop: 7 }} />
          </span>
        </div>
      ))}
    </div>
  );
}

export function AppDetail({ app, userId, date, source, sf, onOpenSession }) {
  const t = useDashTheme();
  const tone = t.c.primary;
  const [facts, setFacts] = React.useState(null);
  const [summary, setSummary] = React.useState("");
  const [purposes, setPurposes] = React.useState([]);
  const [openPurpose, setOpenPurpose] = React.useState(null);
  const [running, setRunning] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showAllSessions, setShowAllSessions] = React.useState(false);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) return;   // one call per open, not per render
    startedRef.current = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/app-detail`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId, date, source, app }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
          throw new Error(err?.message || "Could not open this application.");
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
            if (d.type === "facts") setFacts(d.facts);
            else if (d.type === "summary") setSummary(d.text);
            else if (d.type === "purpose") setPurposes((c) => [...c, d]);
            else if (d.type === "error") setError(d.message);
            else if (d.type === "done") setRunning(false);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally { setRunning(false); }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error && !facts) {
    return (
      <div style={{ fontSize: t.font.size.md, color: t.status.negative, background: t.status.negativeSoft, padding: "10px 12px", borderRadius: t.r.sm }}>
        {error}
      </div>
    );
  }

  if (!facts) return <AppSkeleton tone={tone} t={t} sf={sf} />;

  const sessions = showAllSessions ? facts.sessionsList : facts.sessionsList.slice(0, 6);
  const hidden = facts.sessionsList.length - sessions.length;
  /* A column of six dashes is not information — drop "What was open"
     entirely when no session carries a title or url. */
  const hasOpenInfo = (facts.sessionsList || []).some((sn) => sn.title || sn.url);

  return (
    <div>
      {/* measured, instant */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 18 }}>
        {[
          ["Time", fmtDur(facts.minutes), `${facts.sharePct}% of day`],
          ["Sessions", String(facts.sessions), `longest ${fmtDur(facts.longestMin)}`],
          ["Window", fmtClock(facts.firstMs), `to ${fmtClock(facts.lastMs)}`],
          ["Activity", facts.activity, `${facts.switches} switches`],
        ].map(([l, v, sub], i) => (
          <motion.div key={l}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: t.motion.ease.out, delay: i * 0.05 }}
            style={{
              background: sf.inset, border: `1px solid ${sf.hairline}`,
              borderRadius: t.r.well, padding: "10px 12px",
            }}>
            {/* Same label as the metric tiles one level up — a measurement
                should not change its clothes just because it is nested. */}
            <div style={{ ...t.font.statLabel, color: tone }}>{l}</div>
            <div style={{ fontSize: 17, fontWeight: t.font.weight.semibold, marginTop: 3, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>{v}</div>
            {sub && <div style={{ fontSize: 14.5, color: t.c.textTertiary, marginTop: 2 }}>{sub}</div>}
          </motion.div>
        ))}
      </div>

      {summary && (
        <motion.div
          initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
          style={{
            marginBottom: 18, paddingLeft: 14, borderLeft: `3px solid ${tone}`,
            display: "flex", flexDirection: "column", gap: 10,
          }}>
          {/* One 100-word block was a wall — unreadable at this measure. The
              writer already lands the answer in tiers ("predominantly X…
              alongside this Y… smaller uses Z"), one sentence each, so the
              sentence breaks ARE the structure. Split on them and let the
              first tier carry the weight. Numbering is deliberately absent:
              these are not steps, and numbering them would assert an order
              the content does not have. */}
          {splitSentences(summary).map((line, i) => (
            <p key={i} style={{
              margin: 0,
              fontSize: i === 0 ? 15 : t.font.size.base,
              lineHeight: 1.6,
              color: i === 0 ? t.c.text : t.c.textSecondary,
              fontWeight: i === 0 ? 500 : 400,
              maxWidth: "64ch",
            }}>
              {line}
            </p>
          ))}
        </motion.div>
      )}

      {/* the distinct things it was used FOR — the actual answer */}
      {(purposes.length > 0 || running) && (
        <div style={{ marginBottom: 18 }}>
          <Label t={t}>Pieces of work · open one for its detail</Label>
          {purposes.map((p, i) => {
            const isOpen = openPurpose === i;
            /* Points, not prose. The stream sends structured points; older
               cached runs still carry a `detail` paragraph, split at sentence
               ends so it reads as lines either way. */
            const points = p.points?.length
              ? p.points
              : (p.detail || "").split(/(?<=[.!?])\s+(?=[A-Z0-9])/).map((s) => s.replace(/\.$/, "").trim()).filter(Boolean);
            return (
              <motion.div key={i}
                initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                style={{ borderTop: i ? `1px solid ${sf.hairline}` : "none" }}>
                {/* Compact row — this purpose's detail layer opens on demand. */}
                <button
                  type="button"
                  onClick={() => setOpenPurpose(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  style={{
                    display: "flex", gap: 11, alignItems: "center", width: "100%",
                    padding: "11px 2px", cursor: "pointer",
                    border: "none", background: "transparent", font: "inherit", color: "inherit", textAlign: "left",
                  }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: 99, background: tone, flex: "none",
                    boxShadow: `0 0 0 3px ${tone}22`,
                  }} />
                  <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                    <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.semibold, letterSpacing: "-0.01em" }}>
                      {p.label}
                    </span>
                    {p.at && (
                      <span style={{
                        fontSize: 14, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums",
                        padding: "1px 8px", borderRadius: 99, background: sf.inset, border: `1px solid ${sf.hairline}`,
                      }}>{p.at}</span>
                    )}
                    <span style={{ fontSize: 14, color: t.c.textTertiary }}>
                      {points.length ? `${points.length} detail${points.length === 1 ? "" : "s"}` : ""}
                    </span>
                  </span>
                  <ChevronRight size={15} style={{
                    color: isOpen ? tone : t.c.textTertiary, flexShrink: 0,
                    transform: isOpen ? "rotate(90deg)" : "none",
                    transition: "transform .22s cubic-bezier(.16,1,.3,1), color .18s ease",
                  }} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="body"
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                      style={{ overflow: "hidden" }}>
                      <div style={{ padding: "0 2px 14px 18px" }}>
                        {points.length ? (
                          /* A real list, not flex-ed spans: these ARE a list of
                             findings, so screen readers should hear "4 items".
                             The marker sits on the first line's optical centre
                             via em maths rather than a pinned pixel offset, so
                             it stays aligned if the type scale moves. */
                          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 7 }}>
                            {points.map((pt, j) => (
                              <li key={j} style={{
                                display: "flex", gap: 10, alignItems: "flex-start",
                                fontSize: t.font.size.md, lineHeight: 1.6,
                                color: t.c.textSecondary, maxWidth: "70ch",
                              }}>
                                <span aria-hidden="true" style={{
                                  width: 5, height: 5, borderRadius: 99, background: `${tone}99`,
                                  flex: "none", marginTop: "calc(0.8em - 2.5px)",
                                }} />
                                <span>{pt}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span style={{ fontSize: t.font.size.md, color: t.c.textTertiary }}>
                            The label above is the whole read for this one.
                          </span>
                        )}
                        {p.insight && (
                          /* The conclusion the points add up to. It was set at
                             the same size as the bullets above, so the one line
                             worth reading looked like a fifth bullet — it now
                             steps up in size and contrast and carries the panel. */
                          <div style={{
                            marginTop: 13, padding: "11px 14px", borderRadius: t.r.md,
                            background: t.c.primarySoft, borderLeft: `3px solid ${t.c.primary}`,
                            maxWidth: "70ch",
                          }}>
                            <span style={{ display: "block", ...t.font.statLabel, color: t.c.primary, marginBottom: 4 }}>
                              Key insight
                            </span>
                            <span style={{ fontSize: t.font.size.base, color: t.c.text, lineHeight: 1.6 }}>{p.insight}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
          {running && (
            <div style={{
              display: "flex", gap: 11, paddingTop: purposes.length ? 11 : 0,
              "--wsn-sk": sf.inset,
              "--wsn-sk-hi": t.isDark ? "rgba(255,255,255,.075)" : "rgba(24,32,46,.055)",
            }}>
              <span style={{ width: 7, height: 7, borderRadius: 99, background: `${tone}44`, flex: "none", marginTop: 6 }} />
              <span style={{ flex: 1 }}>
                <Sk w="46%" h={13} r={5} />
                <Sk w="72%" h={11} r={5} style={{ marginTop: 7 }} />
              </span>
            </div>
          )}
        </div>
      )}

      {/* which threads this application fed — the link back to the work */}
      {facts.threadsTouched?.length > 1 && (
        <div style={{ marginBottom: 18 }}>
          <Label t={t}>Fed into</Label>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {facts.threadsTouched.map((th) => (
              <span key={th.key} style={{
                display: "inline-flex", alignItems: "center", gap: 7, padding: "4px 10px",
                borderRadius: 99, background: sf.inset, border: `1px solid ${sf.hairline}`,
                fontSize: 14, color: t.c.textSecondary,
              }}>
                {th.title}
                <b style={{ color: t.c.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtDur(th.minutes)}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {facts.sites?.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <Label t={t}>Sites</Label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {facts.sites.map((u) => (
              <span key={u} style={{
                padding: "3px 9px", borderRadius: t.r.sm, background: sf.inset,
                border: `1px solid ${sf.hairline}`, fontSize: 14.5, color: t.c.textSecondary,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}>{u}</span>
            ))}
          </div>
        </div>
      )}

      {/* every session, as evidence */}
      <div>
        <Label t={t}>{onOpenSession ? "Every session · click one" : "Every session"}</Label>
        <div style={{
          display: "flex", alignItems: "center", gap: 12, paddingBottom: 6,
          ...t.font.microLabel,
          color: t.c.textTertiary,
        }}>
          <span style={{ flex: "none", minWidth: 66 }}>Start</span>
          {hasOpenInfo ? <span style={{ flex: 1 }}>What was open</span> : <span style={{ flex: 1 }} />}
          <span style={{ flex: "none", minWidth: 70 }}>Activity</span>
          <span style={{ flex: "none", minWidth: 54, textAlign: "right" }}>Length</span>
          <span style={{ flex: "none", width: 6 }} />
        </div>
        {sessions.map((sn, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, delay: Math.min(i, 8) * 0.03 }}
            role={onOpenSession ? "button" : undefined}
            onClick={onOpenSession ? () => onOpenSession(sn) : undefined}
            whileHover={onOpenSession ? { x: 3, backgroundColor: sf.inset } : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", margin: "0 -12px",
              borderRadius: 8, backgroundColor: "transparent",
              borderTop: `1px solid ${sf.hairline}`, fontSize: t.font.size.md,
              cursor: onOpenSession ? "pointer" : "default",
            }}>
            <span style={{ flex: "none", minWidth: 66, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums", fontWeight: t.font.weight.medium }}>
              {fmtClock(sn.startMs)}
            </span>
            <span style={{ flex: 1, minWidth: 0, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {hasOpenInfo ? (sn.title || sn.url || "—") : ""}
            </span>
            <span style={{ flex: "none", minWidth: 70, color: t.c.textSecondary }}>{sn.activityLabel}</span>
            <span style={{ flex: "none", minWidth: 54, textAlign: "right", fontWeight: t.font.weight.semibold, color: tone, fontVariantNumeric: "tabular-nums" }}>
              {fmtDur(sn.minutes)}
            </span>
            <span style={{ width: 6, height: 6, borderRadius: 99, flex: "none", background: t.c.primary }}
                  title={sn.src === "app" ? "Desktop App" : "Background Agent"} />
          </motion.div>
        ))}
        {hidden > 0 && (
          <button type="button" onClick={() => setShowAllSessions(true)}
            style={{
              marginTop: 9, background: "none", border: "none", padding: 0, cursor: "pointer",
              color: tone, fontSize: t.font.size.md, fontWeight: t.font.weight.medium,
            }}>
            Show {hidden} more session{hidden === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {facts.entries > 0 && (
        <div style={{ fontSize: 14.5, color: t.c.textTertiary, marginTop: 14 }}>
          Written from {facts.entries.toLocaleString()} tracked entries in this application
        </div>
      )}
    </div>
  );
}

function Label({ children, t }) {
  return (
    <div style={{
      ...t.font.microLabel,
      color: t.c.textTertiary, marginBottom: 9,
    }}>{children}</div>
  );
}

/** A clickable application row. Collapsed it is the accounting line it always
 *  was; open it becomes the answer to the question that line raises. */
export function AppRow({ app, share, widthPct, colour, index, userId, date, source, sf, open, onToggle }) {
  const t = useDashTheme();
  const ref = React.useRef(null);

  /* The list is height-capped, so a row opened near the bottom would unfold
     below the fold of its own container. Nudge it into view once the expand
     animation has had a moment to start. */
  React.useEffect(() => {
    if (!open || !ref.current) return undefined;
    const id = setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 120);
    return () => clearTimeout(id);
  }, [open]);

  return (
    <div ref={ref} style={{ borderTop: index ? `1px solid ${sf.hairline}` : "none" }}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="wsn-approw"
        style={{
          display: "flex", alignItems: "center", gap: 11, width: "100%",
          padding: "9px 0", background: "none", border: "none", cursor: "pointer",
          textAlign: "left", "--row-hue": colour,
        }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 1 150px", minWidth: 92 }}>
          <i style={{ width: 8, height: 8, borderRadius: 2, background: colour, flex: "none" }} />
          <span style={{ fontSize: t.font.size.base, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {app.name}
          </span>
        </span>
        <span style={{ flex: "1 1 40px", minWidth: 34, height: 8, borderRadius: 99, background: sf.insetStrong, overflow: "hidden" }}>
          <motion.i
            initial={{ width: 0 }} animate={{ width: `${widthPct}%` }}
            transition={{ duration: 0.8, ease: t.motion.ease.out, delay: 0.15 + index * 0.05 }}
            style={{ display: "block", height: "100%", background: colour, borderRadius: 99 }} />
        </span>
        <span style={{ flex: "none", minWidth: 52, textAlign: "right", fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, fontVariantNumeric: "tabular-nums" }}>
          {fmtDur(app.minutes)}
        </span>
        <span style={{ flex: "none", minWidth: 34, textAlign: "right", fontSize: 14.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
          {share}%
        </span>
        <motion.span className="wsn-appchev"
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
          style={{
            flex: "none", display: "grid", placeItems: "center", width: 24, height: 24, borderRadius: 99,
            border: `1px solid ${open ? colour : sf.hairline}`,
            background: open ? colour : "transparent",
            color: open ? "#fff" : t.c.textSecondary,
          }}>
          <ChevronRight size={14} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.32, ease: t.motion.ease.out }}
            style={{ overflow: "hidden" }}>
            <div style={{
              padding: "14px 16px 18px", marginBottom: 10, borderRadius: t.r.sm,
              background: sf.inset, borderLeft: `3px solid ${colour}`,
            }}>
              <AppDetail app={app.name} tone={colour} userId={userId} date={date} source={source} sf={sf} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
